import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AttendancePage.css';
import { submitAttendance, getProjectLocations, getTodayAttendance } from '../services/googleScriptServiceCORS';
import { reverseGeocode } from '../services/geocodingService';
import { getSuccessMessage } from '../utils/greetings';
import CameraPreview from './CameraPreview';

function AttendancePageBGD({ employee, apiBaseUrl }) {
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [locationMode, setLocationMode] = useState('project'); // 'project' | 'real'
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [explanation, setExplanation] = useState('');
  const [useManualTime, setUseManualTime] = useState(false);
  const [manualDateTime, setManualDateTime] = useState(() => {
    // format YYYY-MM-DDTHH:mm cho input datetime-local
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [projectLocations, setProjectLocations] = useState([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectLoadError, setProjectLoadError] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [applyBeauty, setApplyBeauty] = useState(true); // Mặc định bật chế độ làm đẹp
  const [beautyIntensity, setBeautyIntensity] = useState(50); // Cường độ làm đẹp (0-100), mặc định 50%
  const [showCameraPreview, setShowCameraPreview] = useState(false); // Hiển thị camera preview

  useEffect(() => {
    // tránh warning exhaustive-deps (không cần đưa function vào deps)
    (async () => {
      await loadTodayAttendance();
      await loadProjectLocations();
    })();
  }, [employee]);

  const loadProjectLocations = async () => {
    setProjectLoading(true);
    setProjectLoadError('');
    try {
      const locations = await getProjectLocations();
      console.log('[VIP] projectLocations loaded:', locations?.length || 0, locations);
      setProjectLocations(locations || []);
    } catch (error) {
      console.error('Error loading project locations:', error);
      setProjectLocations([]);
      setProjectLoadError(error?.message || 'Không thể tải danh sách dự án');
    } finally {
      setProjectLoading(false);
    }
  };

  const loadTodayAttendance = async () => {
    try {
      const useGoogleScript = process.env.REACT_APP_GOOGLE_SCRIPT_URL;
      
      if (useGoogleScript) {
        const todayData = await getTodayAttendance(employee.code);
        setTodayAttendance(todayData);
      } else {
        try {
          const response = await axios.get(`${apiBaseUrl}/api/attendance/today/${employee.id}`);
          setTodayAttendance(response.data);
        } catch (error) {
          if (error.response?.status !== 404) {
            console.error('Error loading today attendance:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error loading today attendance:', error);
    }
  };

  const getRealLocation = async () => {
    if (!navigator.geolocation) {
      setLocationError('Trình duyệt không hỗ trợ định vị');
      return;
    }

    // Geolocation yêu cầu HTTPS (Cloudflare OK) hoặc localhost
    const isSecure =
      window.location.protocol === 'https:' ||
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '[::1]';
    if (!isSecure) {
      setLocationError('⚠️ Geolocation yêu cầu HTTPS hoặc localhost.');
      return;
    }

    setIsGettingLocation(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude, accuracy } = pos.coords;
          const address = await reverseGeocode(latitude, longitude);
          setLocation({
            latitude,
            longitude,
            accuracy: accuracy || null,
            address: address || '',
          });
        } catch (e) {
          const { latitude, longitude, accuracy } = pos.coords;
          setLocation({
            latitude,
            longitude,
            accuracy: accuracy || null,
            address: '',
          });
        } finally {
          setIsGettingLocation(false);
        }
      },
      (err) => {
        let msg = 'Không thể lấy vị trí';
        if (err?.code === err.PERMISSION_DENIED) msg = 'Bạn đã từ chối quyền truy cập vị trí.';
        if (err?.code === err.POSITION_UNAVAILABLE) msg = 'Không thể xác định vị trí.';
        if (err?.code === err.TIMEOUT) msg = 'Hết thời gian chờ lấy vị trí.';
        setLocationError(msg);
        setIsGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  };

  // Hàm random vị trí trong dự án
  const generateRandomLocation = (project) => {
    if (!project || !project.latitude || !project.longitude || !project.radius) {
      return null;
    }

    // Random góc và khoảng cách (sử dụng phân phối đều trong hình tròn)
    const angle = Math.random() * 2 * Math.PI;
    const distance = Math.random() * project.radius; // Khoảng cách từ tâm (mét)
    
    // Chuyển đổi sang độ
    const latDistance = distance / 111000;
    const lngDistance = distance / (111000 * Math.cos(project.latitude * Math.PI / 180));

    // Tính tọa độ mới
    const newLat = project.latitude + (Math.cos(angle) * latDistance);
    const newLng = project.longitude + (Math.sin(angle) * lngDistance);

    return {
      latitude: newLat,
      longitude: newLng,
      // Không thêm text “Vị trí ngẫu nhiên” để tránh ghi vào DB/log
      address: `${project.address || project.name}`,
      accuracy: Math.random() * 20 + 10 // Random accuracy 10-30m
    };
  };

  // Xử lý khi chọn dự án
  const handleProjectSelect = (e) => {
    const projectCode = e.target.value;
    const project = projectLocations.find(p => p.code === projectCode);
    
    if (project) {
      setSelectedProject(project);
      const randomLoc = generateRandomLocation(project);
      setLocation(randomLoc);
      setLocationError(null);
    } else {
      setSelectedProject(null);
      setLocation(null);
    }
  };

  // Xử lý chọn ảnh từ thư viện
  const processImageFile = async (file) => {
    // Kiểm tra loại file
    if (!file.type.startsWith('image/')) {
      alert('Vui lòng chọn file ảnh');
      return;
    }
    
    // Kiểm tra kích thước (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Kích thước ảnh không được vượt quá 10MB');
      return;
    }

    try {
      // TỐI ƯU: Compress ảnh trước khi lưu để giảm dung lượng, tích hợp chế độ làm đẹp
      const { compressImageForAttendance } = await import('../utils/imageCompression');
      const compressedFile = await compressImageForAttendance(file, null, applyBeauty, beautyIntensity);
      
      setImage(compressedFile);
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(compressedFile);
    } catch (error) {
      console.error('Error compressing image:', error);
      // Fallback: sử dụng file gốc nếu compress thất bại
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await processImageFile(file);
    }
  };

  const handleCameraCapture = async (file) => {
    await processImageFile(file);
    setShowCameraPreview(false);
  };

  // Xử lý chụp ảnh
  const handleCaptureImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // Ưu tiên camera sau
    input.onchange = (e) => {
      handleImageSelect(e);
    };
    input.click();
  };

  // Parse datetime-local (YYYY-MM-DDTHH:mm) theo local time một cách ổn định (đặc biệt iOS/Safari)
  const parseDateTimeLocalToMs = (value) => {
    if (!value) return null;
    const [datePart, timePart] = String(value).split('T');
    if (!datePart || !timePart) return null;
    const [y, m, d] = datePart.split('-').map((x) => parseInt(x, 10));
    const [hh, mm] = timePart.split(':').map((x) => parseInt(x, 10));
    if ([y, m, d, hh, mm].some((n) => Number.isNaN(n))) return null;
    return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0).getTime();
  };

  const handleCheckin = async () => {
    if (locationMode === 'project' && !selectedProject) {
      alert('Vui lòng chọn dự án trước khi chấm công');
      return;
    }

    if (!location) {
      alert(locationMode === 'real' ? 'Vui lòng lấy vị trí hiện tại trước khi chấm công' : 'Vui lòng chọn dự án để tạo vị trí');
      return;
    }

    if (!image) {
      alert('Vui lòng chụp hoặc chọn ảnh trước khi chấm công');
      return;
    }

    setLoading(true);
    try {
      const useGoogleScript = process.env.REACT_APP_GOOGLE_SCRIPT_URL;
      const timestampMs = useManualTime ? parseDateTimeLocalToMs(manualDateTime) : null;
      if (useManualTime && !timestampMs) {
        alert('Giờ cài đặt không hợp lệ. Vui lòng chọn lại.');
        setLoading(false);
        return;
      }
      
      if (useGoogleScript) {
        console.log('[VIP] submit checkin with timestamp_ms:', timestampMs);
        const result = await submitAttendance({
          employee_id: employee.id,
          employee_code: employee.code,
          employee_name: employee.name,
          vip: 1,
          type: 'checkin',
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy || null,
          address: location.address,
          image: image,
          explanation: explanation.trim(),
          project_code: locationMode === 'project' ? selectedProject?.code : '',
          project_name: locationMode === 'project' ? selectedProject?.name : '',
          timestamp_ms: timestampMs
        });

        if (result.ok) {
          // Nếu user bật giờ cài đặt nhưng server không nhận, cảnh báo ngay
          if (useManualTime) {
            const usedMs = result.used_timestamp_ms ? Number(result.used_timestamp_ms) : null;
            if (!usedMs || (timestampMs && Math.abs(usedMs - timestampMs) > 60 * 1000)) {
              alert(
                '⚠️ Server KHÔNG ghi theo giờ cài đặt.\n' +
                `- Giờ cài đặt (ms): ${timestampMs}\n` +
                `- Server dùng (ms): ${result.used_timestamp_ms || 'N/A'}\n` +
                `- Server nhận timestamp_ms: ${result.received_timestamp_ms || 'N/A'}\n\n` +
                'Vui lòng redeploy Apps Script đúng version, hoặc gửi ảnh Console log cho mình.'
              );
            }
          }
          const successMessage = getSuccessMessage('checkin');
          alert(successMessage);
          setImage(null);
          setImagePreview(null);
          setExplanation('');
          loadTodayAttendance();
        } else {
          // Kiểm tra xem có yêu cầu giải trình không (hiếm khi xảy ra với VIP)
          if (result.requiresExplanation) {
            alert('Chấm công không hợp lệ. Vui lòng nhập giải trình để tiếp tục.\n' + (result.note || ''));
            // Scroll đến phần giải trình nếu có
            setTimeout(() => {
              const explanationSection = document.querySelector('.explanation-section');
              if (explanationSection) {
                explanationSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 100);
            return; // Không throw error, để user nhập giải trình
          }
          throw new Error(result.error || 'Có lỗi xảy ra khi chấm công');
        }
      } else {
        // Fallback to FastAPI
        const formData = new FormData();
        formData.append('employee_id', employee.id);
        formData.append('type', 'checkin');
        formData.append('latitude', location.latitude);
        formData.append('longitude', location.longitude);
        formData.append('address', location.address);
        if (image) {
          formData.append('image', image);
        }
        if (explanation.trim()) {
          formData.append('explanation', explanation.trim());
        }

        await axios.post(`${apiBaseUrl}/api/attendance/checkin`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        const now = new Date();
        const dateStr = now.toLocaleDateString('vi-VN');
        const timeStr = now.toLocaleTimeString('vi-VN');
        const successMessage = `Xin chào ${employee.name}, chúc mừng bạn đã chấm công vào thành công tại ${selectedProject.name} - ${dateStr} ${timeStr}`;
        alert(successMessage);
        setImage(null);
        setImagePreview(null);
        loadTodayAttendance();
      }
    } catch (error) {
      console.error('Checkin error:', error);
      alert(error.message || error.response?.data?.detail || 'Có lỗi xảy ra khi chấm công vào');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (locationMode === 'project' && !selectedProject) {
      alert('Vui lòng chọn dự án trước khi chấm công');
      return;
    }

    if (!location) {
      alert(locationMode === 'real' ? 'Vui lòng lấy vị trí hiện tại trước khi chấm công' : 'Vui lòng chọn dự án để tạo vị trí');
      return;
    }

    if (!image) {
      alert('Vui lòng chụp hoặc chọn ảnh trước khi chấm công');
      return;
    }

    // Kiểm tra đã checkin chưa
    if (!todayAttendance || !todayAttendance.checkin_time) {
      alert('Vui lòng checkin ca làm việc trước.');
      return;
    }

    setLoading(true);
    try {
      const useGoogleScript = process.env.REACT_APP_GOOGLE_SCRIPT_URL;
      const timestampMs = useManualTime ? parseDateTimeLocalToMs(manualDateTime) : null;
      if (useManualTime && !timestampMs) {
        alert('Giờ cài đặt không hợp lệ. Vui lòng chọn lại.');
        setLoading(false);
        return;
      }
      
      if (useGoogleScript) {
        console.log('[VIP] submit checkout with timestamp_ms:', timestampMs);
        const result = await submitAttendance({
          employee_id: employee.id,
          employee_code: employee.code,
          employee_name: employee.name,
          vip: 1,
          type: 'checkout',
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy || null,
          address: location.address,
          image: image,
          explanation: explanation.trim(),
          project_code: locationMode === 'project' ? selectedProject?.code : '',
          project_name: locationMode === 'project' ? selectedProject?.name : '',
          timestamp_ms: timestampMs
        });

        if (result.ok) {
          if (useManualTime) {
            const usedMs = result.used_timestamp_ms ? Number(result.used_timestamp_ms) : null;
            if (!usedMs || (timestampMs && Math.abs(usedMs - timestampMs) > 60 * 1000)) {
              alert(
                '⚠️ Server KHÔNG ghi theo giờ cài đặt.\n' +
                `- Giờ cài đặt (ms): ${timestampMs}\n` +
                `- Server dùng (ms): ${result.used_timestamp_ms || 'N/A'}\n` +
                `- Server nhận timestamp_ms: ${result.received_timestamp_ms || 'N/A'}\n\n` +
                'Vui lòng redeploy Apps Script đúng version, hoặc gửi ảnh Console log cho mình.'
              );
            }
          }
          const successMessage = getSuccessMessage('checkout');
          alert(successMessage);
          setImage(null);
          setImagePreview(null);
          setExplanation('');
          loadTodayAttendance();
        } else {
          // Kiểm tra xem có yêu cầu giải trình không (hiếm khi xảy ra với VIP)
          if (result.requiresExplanation) {
            alert('Chấm công không hợp lệ. Vui lòng nhập giải trình để tiếp tục.\n' + (result.note || ''));
            // Scroll đến phần giải trình nếu có
            setTimeout(() => {
              const explanationSection = document.querySelector('.explanation-section');
              if (explanationSection) {
                explanationSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 100);
            return; // Không throw error, để user nhập giải trình
          }
          throw new Error(result.error || 'Có lỗi xảy ra khi chấm công');
        }
      } else {
        // Fallback to FastAPI
        const formData = new FormData();
        formData.append('employee_id', employee.id);
        formData.append('type', 'checkout');
        formData.append('latitude', location.latitude);
        formData.append('longitude', location.longitude);
        formData.append('address', location.address);
        if (image) {
          formData.append('image', image);
        }
        if (explanation.trim()) {
          formData.append('explanation', explanation.trim());
        }

        await axios.post(`${apiBaseUrl}/api/attendance/checkout`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        const now = new Date();
        const dateStr = now.toLocaleDateString('vi-VN');
        const timeStr = now.toLocaleTimeString('vi-VN');
        const successMessage = `Xin chào ${employee.name}, chúc mừng bạn đã chấm công ra thành công tại ${selectedProject.name} - ${dateStr} ${timeStr}`;
        alert(successMessage);
        setImage(null);
        setImagePreview(null);
        loadTodayAttendance();
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert(error.message || error.response?.data?.detail || 'Có lỗi xảy ra khi chấm công ra');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="attendance-page">
      <div className="attendance-container">
        <h2 className="attendance-title">Chấm Công VIP</h2>
        
        {/* Cài đặt giờ chấm công (VIP) */}
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="checkbox"
              checked={useManualTime}
              onChange={(e) => setUseManualTime(e.target.checked)}
              disabled={loading}
            />
            Dùng giờ cài đặt để ghi DB
          </label>
          {useManualTime && (
            <input
              type="datetime-local"
              value={manualDateTime}
              onChange={(e) => setManualDateTime(e.target.value)}
              className="form-control"
              disabled={loading}
              style={{ marginTop: '10px' }}
            />
          )}
        </div>

        {/* Thông tin nhân viên */}
        <div className="employee-info">
          <p><strong>Nhân viên:</strong> {employee.name}</p>
          {todayAttendance && todayAttendance.checkin_time && (
            <p><strong>Giờ vào:</strong> {new Date(todayAttendance.checkin_time).toLocaleString('vi-VN')}</p>
          )}
          {todayAttendance && todayAttendance.checkout_time && (
            <p><strong>Giờ ra:</strong> {new Date(todayAttendance.checkout_time).toLocaleString('vi-VN')}</p>
          )}
        </div>

        {/* Chọn chế độ vị trí */}
        <div className="form-group">
          <label><strong>Chế độ vị trí:</strong></label>
          <div style={{ display: 'flex', gap: '14px', marginTop: '8px', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="radio"
                name="vip-location-mode"
                value="project"
                checked={locationMode === 'project'}
                onChange={() => {
                  setLocationMode('project');
                  setLocation(null);
                  setLocationError(null);
                }}
                disabled={loading}
              />
              Chọn dự án (random vị trí)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="radio"
                name="vip-location-mode"
                value="real"
                checked={locationMode === 'real'}
                onChange={() => {
                  setLocationMode('real');
                  setSelectedProject(null);
                  setLocation(null);
                  setLocationError(null);
                }}
                disabled={loading}
              />
              Dùng vị trí thực tế (GPS)
            </label>
          </div>
          {locationMode === 'real' && (
            <div style={{ marginTop: '10px' }}>
              <button
                type="button"
                onClick={getRealLocation}
                className="btn-capture"
                disabled={loading || isGettingLocation}
              >
                {isGettingLocation ? 'Đang lấy vị trí...' : '📍 Lấy vị trí hiện tại'}
              </button>
              {location && (
                <div style={{ marginTop: '10px', fontSize: '14px' }}>
                  <div><strong>Tọa độ:</strong> {location.latitude?.toFixed?.(6)}, {location.longitude?.toFixed?.(6)}</div>
                  {location.address ? <div><strong>Địa chỉ:</strong> {location.address}</div> : null}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Chọn dự án */}
        <div className="form-group" style={{ display: locationMode === 'project' ? 'block' : 'none' }}>
          <label htmlFor="project-select">Chọn dự án đang hoạt động:</label>
          {projectLoadError && (
            <div className="error-message" style={{ marginBottom: '10px' }}>
              {projectLoadError}
            </div>
          )}
          <select
            id="project-select"
            value={selectedProject?.code || ''}
            onChange={handleProjectSelect}
            className="form-control"
            disabled={loading || projectLoading}
          >
            <option value="">
              {projectLoading ? 'Đang tải dự án...' : '-- Chọn dự án --'}
            </option>
            {projectLocations.map((project) => (
              <option key={project.code} value={project.code}>
                {project.name} - {project.address}
              </option>
            ))}
          </select>
          {selectedProject && (
            <div className="project-info" style={{ marginTop: '10px', padding: '10px', backgroundColor: '#e8f5e9', borderRadius: '5px' }}>
              <p><strong>Dự án:</strong> {selectedProject.name}</p>
              <p><strong>Địa chỉ:</strong> {selectedProject.address}</p>
              {location && (
                <>
                  <p><strong>Vị trí:</strong> {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}</p>
                  <button
                    type="button"
                    onClick={() => {
                      const newRandomLoc = generateRandomLocation(selectedProject);
                      setLocation(newRandomLoc);
                    }}
                    className="btn-random-location"
                    style={{ marginTop: '10px', padding: '8px 16px', backgroundColor: '#4caf50', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                  >
                    🎲 Random lại vị trí
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Ảnh */}
        <div className="form-group">
          <label>Ảnh chấm công:</label>
          <div className="image-actions">
            <button
              type="button"
              onClick={handleCaptureImage}
              className="btn-capture"
              disabled={loading}
            >
              📷 Chụp ảnh
            </button>
            <button
              type="button"
              onClick={() => setShowCameraPreview(true)}
              className="btn-camera-preview"
              disabled={loading}
              title="Mở camera với làm đẹp real-time"
            >
              ✨ Camera làm đẹp
            </button>
            <label htmlFor="image-select" className="btn-select-image">
              📁 Chọn ảnh từ thư viện
            </label>
            <input
              id="image-select"
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              style={{ display: 'none' }}
              disabled={loading}
            />
          </div>

          <div className="beauty-controls-container">
            <div className="beauty-toggle-container">
              <label className="beauty-toggle-label">
                <span className="beauty-toggle-text">Chế độ làm đẹp ✨</span>
                <div className="beauty-switch">
                  <input 
                    type="checkbox" 
                    checked={applyBeauty}
                    onChange={(e) => setApplyBeauty(e.target.checked)}
                  />
                  <span className="beauty-slider"></span>
                </div>
              </label>
            </div>
            
            {applyBeauty && (
              <div className="beauty-intensity-container">
                <label className="beauty-intensity-label">
                  <span className="beauty-intensity-text">Cường độ: {beautyIntensity}%</span>
                </label>
                <div className="beauty-slider-wrapper">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={beautyIntensity}
                    onChange={(e) => setBeautyIntensity(parseInt(e.target.value))}
                    className="beauty-intensity-slider"
                  />
                  <div className="beauty-slider-labels">
                    <span>Nhẹ</span>
                    <span>Vừa</span>
                    <span>Mạnh</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          {imagePreview && (
            <div className="image-preview-container">
              <img src={imagePreview} alt="Preview" className="image-preview" />
              <button
                type="button"
                onClick={() => {
                  setImage(null);
                  setImagePreview(null);
                }}
                className="btn-remove-image"
              >
                ✕ Xóa ảnh
              </button>
            </div>
          )}
        </div>

        {/* Giải trình */}
        <div className="form-group">
          <label htmlFor="explanation">Giải trình (nếu có):</label>
          <textarea
            id="explanation"
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            placeholder="Nhập giải trình nếu cần..."
            className="form-control"
            rows="3"
            disabled={loading}
          />
        </div>

        {/* Nút chấm công */}
        <div className="attendance-actions">
          {!todayAttendance || !todayAttendance.checkin_time ? (
            <button
              onClick={handleCheckin}
              disabled={
                loading ||
                (locationMode === 'project' && !selectedProject) ||
                !location ||
                !image
              }
              className="btn-checkin"
            >
              {loading ? 'Đang xử lý...' : 'Chấm Công Vào'}
            </button>
          ) : todayAttendance.checkout_time ? (
            <div className="attendance-complete">
              <p>✅ Đã hoàn thành chấm công hôm nay</p>
            </div>
          ) : (
            <button
              onClick={handleCheckout}
              disabled={
                loading ||
                (locationMode === 'project' && !selectedProject) ||
                !location ||
                !image
              }
              className="btn-checkout"
            >
              {loading ? 'Đang xử lý...' : 'Chấm Công Ra'}
            </button>
          )}
        </div>

        {locationError && (
          <div className="error-message">{locationError}</div>
        )}
      </div>

      {/* Camera Preview với làm đẹp real-time */}
      {showCameraPreview && (
        <CameraPreview
          onCapture={handleCameraCapture}
          onClose={() => setShowCameraPreview(false)}
          initialBeautyIntensity={beautyIntensity}
          applyBeauty={applyBeauty}
        />
      )}
    </div>
  );
}

export default AttendancePageBGD;
